// Google Analytics 4 — thin gtag.js wrapper. See ANALYTICS.md (umbrella repo).
//
// Analytics is **optional at three levels**, checked in this order:
//   1. no `REACT_APP_GA_MEASUREMENT_ID` → never loads (local dev, unconfigured tiers)
//   2. `REACT_APP_ANALYTICS_ENABLED=false` → never loads even with an id (kill switch)
//   3. a per-browser opt-out in localStorage → never loads, or disables a live tag
// Every exported call is a safe no-op when analytics is off, so call sites need no
// guards of their own.
//
// SPA page_views are emitted manually from usePageTracking (`send_page_view` is off,
// and the stream's "page changes based on browser history events" setting is off —
// ANALYTICS.md B9 — otherwise every navigation counts twice).
import config from "../config";
import type { EventName, EventPayloads } from "./events";
import { pageTitleFor, sanitizeReferrer, sanitizeRoute } from "./sanitize";

export type AppName = "supplier" | "admin" | "agent";

const MEASUREMENT_ID = config.GA_MEASUREMENT_ID;
const ENV = config.ENV;
const DEBUG = config.ANALYTICS_DEBUG;

/** Per-browser opt-out. Survives reloads; scoped to this origin. */
const OPT_OUT_KEY = "concierge.analytics.opt_out";
/** Debug mirror, inspectable in DevTools (console is muted in production builds). */
const DEBUG_BUFFER_KEY = "__conciergeAnalytics";
const DEBUG_BUFFER_LIMIT = 200;

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    [DEBUG_BUFFER_KEY]?: Array<{ command: string; args: any[] }>;
  }
}

let app: AppName | null = null;
/** True once gtag.js has been injected and configured. */
let started = false;
/** Scrubbed route of the page we are currently on — the next page_view's referrer. */
let currentRoute = "";

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

function readOptOut(): boolean {
  if (!hasWindow()) return false;
  try {
    return window.localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    // Storage can throw (Safari private mode, blocked third-party context).
    return false;
  }
}

/** Configured to run, ignoring the per-browser opt-out. */
function configured(): boolean {
  return Boolean(MEASUREMENT_ID) && config.ANALYTICS_ENABLED && hasWindow();
}

/** True when events will actually reach GA. */
export function isEnabled(): boolean {
  return started && !readOptOut();
}

function record(command: string, args: any[]): void {
  if (!DEBUG) return;
  const buffer = (window[DEBUG_BUFFER_KEY] = window[DEBUG_BUFFER_KEY] || []);
  buffer.push({ command, args });
  if (buffer.length > DEBUG_BUFFER_LIMIT) buffer.shift();
}

function gtag(...args: any[]): void {
  record(String(args[0]), args.slice(1));
  window.gtag(...args);
}

/**
 * Overrides the URL fields gtag would otherwise read from the document. This is the
 * load-bearing part of B4: `page_location`, `page_referrer` and `page_title` are
 * attached to *every* hit from the document if we don't set them, so a reset-password
 * token in the URL would ride along on unrelated events. Set globally, not per-event.
 */
/**
 * Sanitize a route and put it under its app's segment — `/supplier/login`,
 * `/admin/login`, `/agent/login`.
 *
 * All three portals report into one GA property and their raw paths collide:
 * `/login` is three different screens, and the default Pages and screens report
 * stacks them into a single row. Telling them apart otherwise means a user-scoped
 * custom dimension, which is not retroactive and re-attributes anyone who uses two
 * portals to whichever they touched last. Putting the app in the path makes the
 * report answer the question by itself, with no dimension to register and nothing
 * to configure.
 *
 * Applied here rather than inside `sanitizeRoute` so there is exactly one place a
 * route becomes app-scoped, and so the sanitizer stays a pure scrubber. Falls back
 * to the bare route if `init` has not run, which cannot happen in practice — every
 * emitter is gated on `isEnabled()`.
 */
function appScopedRoute(pathname: string, search = ""): string {
  const route = sanitizeRoute(pathname, search);
  if (!app) return route;
  const q = route.indexOf("?");
  const path = q === -1 ? route : route.slice(0, q);
  const rest = q === -1 ? "" : route.slice(q);
  const prefix = `/${app}`;
  const stripped = path.startsWith(prefix + '/') || path === prefix ? path.slice(prefix.length) : path;
  return `${prefix}${stripped === '/' ? '' : stripped}${rest}`;
}

function setPageContext(route: string, referrer: string): void {
  gtag("set", {
    page_location: window.location.origin + route,
    page_referrer: referrer,
    page_title: pageTitleFor(route),
  });
}

export function init(opts: { app: AppName }): void {
  app = opts.app;
  if (started || !configured() || readOptOut()) return;

  window.dataLayer = window.dataLayer || [];
  // Canonical gtag shim — must push the `arguments` object, not an array.
  window.gtag = function gtagShim() {
    window.dataLayer.push(arguments);
  };

  // Consent Mode v2 defaults, before any config or event command (order in the
  // dataLayer is what matters, not wall-clock time). These portals are authenticated
  // internal B2B tools with no advertising, so every ad_* signal is denied outright
  // and there is no banner to collect a choice with; `analytics_storage` is granted
  // under the operator relationship, revocable per browser via setOptOut(). See B8.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
    functionality_storage: "granted",
    personalization_storage: "denied",
    security_storage: "granted",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());

  const origin = window.location.origin;
  setPageContext(
    appScopedRoute(window.location.pathname, window.location.search),
    sanitizeReferrer(document.referrer, origin)
  );

  gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    // No Google Signals / ad personalisation for a B2B operator tool.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...(DEBUG ? { debug_mode: true } : {}),
  });
  gtag("set", "user_properties", { app, env: ENV });

  started = true;
}

/**
 * `path` may include a query string; it is scrubbed here rather than at the call
 * site so no caller can bypass it. There is deliberately no `title` argument —
 * titles are derived from the route, because on detail screens the real document
 * title can contain a customer name (B4).
 */
export function trackPageView(path: string): void {
  if (!isEnabled()) return;

  const [rawPath, rawSearch] = splitPath(path);
  const route = appScopedRoute(rawPath, rawSearch);
  const origin = window.location.origin;
  // The previous page is a better SPA referrer than `document.referrer` (which is
  // frozen at the entry page) and it is already scrubbed.
  const referrer = currentRoute
    ? origin + currentRoute
    : sanitizeReferrer(document.referrer, origin);

  setPageContext(route, referrer);
  gtag("event", "page_view", {
    page_path: route,
    page_location: origin + route,
    page_referrer: referrer,
    page_title: pageTitleFor(route),
  });
  currentRoute = route;
}

function splitPath(path: string): [string, string] {
  const index = path.indexOf("?");
  if (index === -1) return [path, ""];
  return [path.slice(0, index), path.slice(index)];
}

/**
 * Typed event emitter (B7). The payload type is looked up from the event name, so
 * a wrong or extra param is a compile error rather than a silent GA custom
 * dimension nobody registered.
 */
export function track<K extends EventName>(name: K, params: EventPayloads[K]): void {
  if (!isEnabled()) return;
  gtag("event", name, params ?? {});
}

/**
 * Identify the operator. PII (email/name/phone) is intentionally never sent — only
 * an opaque id and coarse role/supplier dimensions. `session` may store the profile
 * flat or nested under `.user`, and the role may arrive as a string, an object with
 * `.name`, or admin-fe's `roles: string[]`.
 */
export function setUser(session: unknown): void {
  if (!isEnabled() || !session) return;
  const s = session as Record<string, any>;
  const u = (s.user ?? s) as Record<string, any>;
  const id = u?.id ?? u?._id ?? u?.userId;
  if (!id) return;

  gtag("set", { user_id: String(id) });

  // No default role: `"supplier"` here meant every admin and agent whose shape we
  // failed to read was reported as a supplier (B1). An absent dimension is a
  // visible gap; a wrong one silently corrupts every role-segmented report.
  const role = readRole(u);
  const supplierId = u?.supplier?.id ?? u?.supplierId;
  gtag("set", "user_properties", {
    app,
    env: ENV,
    ...(role ? { role } : {}),
    ...(supplierId ? { supplier_id: String(supplierId) } : {}),
  });
}

function readRole(u: Record<string, any>): string | undefined {
  const raw = u?.role?.name ?? u?.role ?? u?.roles?.[0] ?? u?.userType;
  if (raw === undefined || raw === null) return undefined;
  const value = typeof raw === "string" ? raw : raw?.name;
  return value ? String(value) : undefined;
}

/**
 * Forget the operator. Clears every identity dimension, not just `user_id` — a
 * stale `role`/`supplier_id` would otherwise be stamped onto the next person to
 * use the browser (B5).
 */
export function clearUser(): void {
  if (!isEnabled()) return;
  gtag("set", { user_id: null });
  gtag("set", "user_properties", {
    role: null,
    supplier_id: null,
    app,
    env: ENV,
  });
}

/** Current per-browser opt-out state. */
export function isOptedOut(): boolean {
  return readOptOut();
}

/**
 * Opt this browser in or out. Takes effect immediately on a live tag: gtag's own
 * `ga-disable-<id>` flag stops collection, and the consent update covers anything
 * that reads consent rather than the flag. Persisted, so the next load never even
 * injects the script.
 */
export function setOptOut(optOut: boolean): void {
  if (!hasWindow()) return;
  try {
    if (optOut) window.localStorage.setItem(OPT_OUT_KEY, "1");
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    // Non-persistent opt-out is still better than none for this page's lifetime.
  }
  if (MEASUREMENT_ID) {
    (window as any)[`ga-disable-${MEASUREMENT_ID}`] = optOut;
  }
  if (started) {
    window.gtag("consent", "update", {
      analytics_storage: optOut ? "denied" : "granted",
    });
  }
  // Opting back in mid-session on a build that skipped init() needs the tag loaded.
  if (!optOut && !started && app) init({ app });
}
